import React, { startTransition, useMemo, useState, Fragment, useEffect } from 'react';
import { EllipsisOutlined } from '@ant-design/icons';
import useResize from '@kne/use-resize';
import { Button, Dropdown, Space, Tooltip } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import useRefCallback from '@kne/use-ref-callback';
import classnames from 'classnames';
import LoadingButton from '../LoadingButton';
import ConfirmButton from '../ConfirmButton';
import pick from 'lodash/pick';
import areaWidthComputed from './areaWidthComputed';
import style from './style.module.scss';
import { createWithIntlProvider, useIntl } from '@kne/react-intl';
import zhCn from '../locale/zh-CN';

const ButtonGroup = createWithIntlProvider(
  'zh-CN',
  zhCn,
  'button-group'
)(p => {
  const { formatMessage } = useIntl();
  const { list: originalList, more, moreType, compact, showLength: showLengthProps, getPopupContainer, trigger, itemClassName, ...props } = Object.assign({}, p);
  const list = useMemo(() => originalList.filter(item => !item?.hidden), [originalList]);
  const spaceProps = pick(props, ['size', 'split', 'align', 'style']);
  const [showLengthState, setShowLength] = useState(list.length);
  const showLength = Number.isInteger(showLengthProps) ? showLengthProps : showLengthState;
  const computedLength = useRefCallback(() => {
    const el = targetRef.current,
      moreEl = moreRef.current,
      widthEl = ref.current;
    if (!el || !widthEl) {
      return;
    }

    const buttonEls = el.querySelectorAll('.button-group-item');
    if (buttonEls.length === 0) {
      return;
    }

    const amountWidth = Math.floor(widthEl.clientWidth),
      moreBtnWidth = moreEl?.clientWidth || 0,
      buttonWidthList = [].map.call(buttonEls, el => el.offsetWidth);
    const targetLength = areaWidthComputed({
      amountWidth,
      moreBtnWidth,
      buttonWidthList,
      spaceProps,
      compact
    });
    startTransition(() => {
      setShowLength(prev => (prev === targetLength ? prev : targetLength));
    });
  });
  const ref = useResize(computedLength);
  const targetRef = useResize(computedLength);
  const moreRef = useResize(computedLength);
  const otherList = list.slice(showLength);

  useEffect(() => {
    setShowLength(list.length);
    computedLength();
  }, [list, computedLength]);

  const renderButton = (renderItem, index, isDropdown) => {
    if (typeof renderItem === 'function') {
      return renderItem(
        {
          key: index,
          className: classnames('button-group-item', style['btn-item'], itemClassName)
        },
        { isDropdown }
      );
    }
    const { className, confirm, buttonComponent, tooltipProps, hidden, isDelete, isModal, ...props } = renderItem;

    const isConfirm = confirm || !!props.message || isDelete;
    const CurrentButton = buttonComponent || (isConfirm ? ConfirmButton : LoadingButton);
    const currentButton = (
      <CurrentButton
        {...Object.assign(
          {},
          props,
          isConfirm
            ? {
                danger: isDelete !== false,
                isDelete: isDelete !== false,
                ...(isModal || isDropdown
                  ? {
                      isModal: true
                    }
                  : {})
              }
            : {},
          isDropdown ? { type: 'default' } : {}
        )}
        key={index}
        className={classnames('button-group-item', className, itemClassName)}
      />
    );
    return tooltipProps ? <Tooltip {...tooltipProps}>{currentButton}</Tooltip> : currentButton;
  };

  const renderMoreButton = () =>
    more ||
    (moreType === 'link' ? (
      <Button type="link" className={classnames('button-group-item', itemClassName, style['more-link-btn'])}>
        <EllipsisOutlined style={{ fontSize: '16px' }} />
      </Button>
    ) : (
      <Button>
        {formatMessage({ id: 'more' })}
        <DownOutlined />
      </Button>
    ));

  const SpaceComponent = compact ? Space.Compact : Space;

  return (
    <div className={style['button-group']}>
      <div className={style['width-container']} ref={ref} />
      <div className={style['hidden-container']}>
        <div className={style['hidden-inner']} ref={moreRef}>
          {renderMoreButton()}
        </div>
        <div className={style['hidden-inner']} ref={targetRef}>
          <SpaceComponent {...spaceProps}>
            {list.map((item, index) => (
              <Fragment key={index}>{renderButton(item, index, false)}</Fragment>
            ))}
          </SpaceComponent>
        </div>
      </div>
      <SpaceComponent {...spaceProps}>
        {list.slice(0, showLength).map((item, index) => (
          <Fragment key={index}>{renderButton(item, index, false)}</Fragment>
        ))}
        {otherList.length > 0 && (
          <Dropdown
            getPopupContainer={getPopupContainer}
            trigger={trigger}
            rootClassName={style['menu-list']}
            menu={{
              items: otherList.map((item, index) => {
                return {
                  key: index,
                  label: renderButton(item, index, true)
                };
              })
            }}
          >
            {renderMoreButton()}
          </Dropdown>
        )}
      </SpaceComponent>
    </div>
  );
});

export default ButtonGroup;
